import React, { useState, useEffect } from "react";
import { useGlobalState } from "../helpers/GlobalStateProvider";
import { useModalState } from "../helpers/ModalProvider";
import { Badge, Box, Flex, Link, Stack, Text } from "@chakra-ui/react";
import { LuChevronDown, LuInfo, LuUpload } from "react-icons/lu";
import { useSwitchChain, useWalletClient } from "wagmi";
import { getWalletClient } from "wagmi/actions";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { config } from "..";
import { Button } from "../components/ui/button";
import { formatDecimals } from "../helpers/utils";
import { broadcastClaimRewardsTx } from "../helpers/claimRewards";

const StakingStatsContainer: React.FC = () => {
  const { stakeMorAndConnectWallet, info, morRewards, rewardContractInfo, baseRewards, totalPoints, setSelectedChainId, setTransactionDetails, updatePointsAndRewards } = useGlobalState();
  const { setWhatIsModalVisible, showSelectNetworkDialog, setClaimBroadcastModalVisible, showErrorMessage } = useModalState();

  const { switchChainAsync } = useSwitchChain({ config });
  const { data: walletClient } = useWalletClient();
  const { openConnectModal } = useConnectModal();

  const baseRewardContractInfo = rewardContractInfo?.baseRewardContractInfo
  const arbitrumRewardContractInfo = rewardContractInfo?.arbitrumRewardContractInfo

  const totalPendingBaseRewards = (baseRewards ?? 0) - parseFloat(baseRewardContractInfo?.totalRewardsWithdrawn ?? '0');

  const availablePendingBaseRewards = parseFloat(baseRewardContractInfo?.totalRewardsEarned ?? '0') - parseFloat(baseRewardContractInfo?.totalRewardsWithdrawn ?? '0')

  // Frozen pre-cutoff Arbitrum rewards (distribution ended Jan 2026). Surfaced
  // and claimable so legacy stakers can withdraw what they earned before the
  // cutoff; it does not grow (nothing accrues on Arbitrum post-cutoff).
  const availablePendingArbRewards = parseFloat(arbitrumRewardContractInfo?.totalRewardsEarned ?? '0') - parseFloat(arbitrumRewardContractInfo?.totalRewardsWithdrawn ?? '0')
  const canWithdrawAnyRewards = availablePendingBaseRewards > 0 || availablePendingArbRewards > 0
  // Total claimable now across chains — the single source of truth for the
  // Claim button and the "Available" figure so they never disagree.
  const totalClaimableRewards = availablePendingBaseRewards + availablePendingArbRewards

  const totalPendingRewards = totalPendingBaseRewards;

  // Claim rewards: auto-detect which chain(s) have pending rewards. One chain
  // -> switch to it and broadcast the claim directly. Both chains -> let the
  // user pick via the SelectNetworkDialog. ClaimBroadcastModal shows the
  // resulting transaction as before.
  const handleClaimRewards = () => {
    const hasBase = availablePendingBaseRewards > 0;
    const hasArbitrum = availablePendingArbRewards > 0;
    if (!hasBase && !hasArbitrum) return;

    if (hasBase && hasArbitrum) {
      showSelectNetworkDialog(
        [
          { chainId: 8453, name: "Base", amount: availablePendingBaseRewards },
          { chainId: 42161, name: "Arbitrum", amount: availablePendingArbRewards },
        ],
        "claim",
      );
      return;
    }

    const chainId = hasArbitrum ? 42161 : 8453;
    void claimOnChain(chainId);
  };

  // Await the network switch before broadcasting: firing both in the same
  // tick races the wallet's switch prompt against the tx prompt, and the
  // pre-switch walletClient would read the rewards contract on the wrong
  // chain (bogus withdraw amount / reverted tx).
  const claimOnChain = async (chainId: 8453 | 42161) => {
    try {
      await switchChainAsync({ chainId });
    } catch {
      // User rejected the network switch — stay put, nothing to broadcast.
      return;
    }
    setSelectedChainId(chainId);
    // Re-resolve the wallet client on the new chain; the hook value in this
    // closure still points at the pre-switch chain.
    const freshClient = (await getWalletClient(config, { chainId }).catch(() => null)) ?? walletClient;
    // Only open the broadcast modal once a tx actually went out.
    // broadcastClaimRewardsTx early-returns (no wallet / user rejects) without
    // setting transactionDetails; opening the modal then would leave its
    // visible flag stuck true with nothing to render, and surface a surprise
    // modal if transactionDetails gets populated by a later flow.
    const txHash = await broadcastClaimRewardsTx(freshClient, chainId, {
      setTransactionDetails,
      updatePointsAndRewards,
      showErrorMessage,
      openConnectModal,
    });
    if (txHash) setClaimBroadcastModalVisible(true);
  };

  // Local states for the animated display
  const [displayedPoints, setDisplayedPoints] = useState<number>(0);
  const [displayedMorRewards, setDisplayedMorRewards] = useState<number>(0);

  // Animate displayedPoints
  useEffect(() => {
    if (totalPoints == null || info?.pointsPerDay == null) {
      setDisplayedPoints(0);
      return;
    }

    const { pointsPerDay } = info;
    setDisplayedPoints(totalPoints);

    const pointsPerMs = pointsPerDay / 86400000;
    const pointsPer200Ms = pointsPerMs * 200;
    const intervalId = setInterval(() => {
      if (totalPoints > 0) {
        setDisplayedPoints((prev) => prev + pointsPer200Ms);
      }
    }, 200);

    return () => clearInterval(intervalId);
  }, [totalPoints, info?.pointsPerDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animate displayedMorRewards
  useEffect(() => {
    if (totalPendingRewards == null || isNaN(totalPendingRewards) || info?.morRewardsPerDay == null) {
      setDisplayedMorRewards(0);
      return;
    }

    const { morRewardsPerDay } = info;
    setDisplayedMorRewards(totalPendingRewards);

    const rewardsPerS = morRewardsPerDay / 86400;
    const intervalId = setInterval(() => {
      if (morRewards > 0) {
        setDisplayedMorRewards((prev) => prev + rewardsPerS);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [totalPendingRewards, info?.morRewardsPerDay, morRewards]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isExpanded, setIsExpanded] = useState(false);

  const stakedBalance = parseFloat(info?.stakedBalance ?? '0');
  // Per-chain staked principal. stakedBalance (staked_mor) is Base-only — the
  // backend deliberately keeps Arbitrum out of it so Arbitrum never counts
  // toward tier / points / rewards. stakedArbitrumBalance is read on-chain
  // (exit-only) so legacy Arbitrum stakers can see and withdraw their locked
  // principal even though Arbitrum staking is discontinued.
  const stakedArbitrumBalance = parseFloat(info?.stakedArbitrumBalance ?? '0');
  const stakedBaseBalance = parseFloat(info?.stakedBaseBalance ?? '0');
  const baseStaked = Math.max(stakedBalance, stakedBaseBalance);
  const hasArbitrumStake = stakedArbitrumBalance > 0;
  const hasAnyStake = baseStaked > 0 || hasArbitrumStake;
  const totalStakedDisplay = baseStaked + stakedArbitrumBalance;
  const bothChainsStaked = baseStaked > 0 && hasArbitrumStake;
  // Show the full staking window whenever the user has a positive balance in
  // any of the three tracked stats — Staked MOR (either chain), MOR Rewards
  // (pending/claimable, either chain), or CoinCap Points. Only fall back to
  // the minimized banner when all three are zero.
  const hasMorRewardsBalance = (totalPendingRewards ?? 0) > 0 || canWithdrawAnyRewards;
  const hasPointsBalance = (totalPoints ?? 0) > 0;
  const showFullWindow = hasAnyStake || hasMorRewardsBalance || hasPointsBalance;
  const morRewardsPerDay = info?.morRewardsPerDay ?? 0;
  const morRewardsPerMonth = morRewardsPerDay * 30;
  const morRewardsPerYear = morRewardsPerDay * 365;

  // Next withdraw date: prefer a backend-provided timestamp if it ever becomes
  // available on `info` or the reward contract info payload. Otherwise, try to
  // derive it from a last-deposit epoch-seconds timestamp plus the on-chain
  // 7-day claim lock period (see StakeModal1/UISection). If neither is present
  // we render nothing rather than a stale hardcoded date.
  const MOR_WITHDRAW_LOCK_MS = 7 * 24 * 60 * 60 * 1000;
  const ordinalSuffix = (day: number): string => {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };
  const formatWithdrawDate = (date: Date): string => {
    const month = date.toLocaleString("en-US", { month: "long" });
    const day = date.getDate();
    return `${month} ${day}${ordinalSuffix(day)}, ${date.getFullYear()}`;
  };
  const resolveNextWithdrawDate = (): Date | null => {
    // 1. Direct ISO/epoch fields the backend may add in the future.
    const directCandidates: unknown[] = [
      (info as any)?.nextWithdrawDate,
      (info as any)?.withdrawUnlockAt,
      (baseRewardContractInfo as any)?.nextWithdrawDate,
      (baseRewardContractInfo as any)?.withdrawUnlockAt,
    ];
    for (const raw of directCandidates) {
      if (raw == null || raw === "") continue;
      const asNumber = typeof raw === "number" ? raw : Number(raw);
      if (Number.isFinite(asNumber) && asNumber > 0) {
        // Heuristic: treat small values as seconds, larger as ms.
        const ms = asNumber < 1e12 ? asNumber * 1000 : asNumber;
        return new Date(ms);
      }
      if (typeof raw === "string") {
        const parsed = new Date(raw);
        if (!Number.isNaN(parsed.getTime())) return parsed;
      }
    }
    // 2. Derive from a last-deposit timestamp (epoch seconds) + lock period.
    const lastDepositRaw: unknown =
      (info as any)?.lastDepositTime ??
      (baseRewardContractInfo as any)?.lastDepositTime;
    if (lastDepositRaw != null && lastDepositRaw !== "") {
      const lastDepositSec =
        typeof lastDepositRaw === "number"
          ? lastDepositRaw
          : parseInt(String(lastDepositRaw), 10);
      if (Number.isFinite(lastDepositSec) && lastDepositSec > 0) {
        return new Date(lastDepositSec * 1000 + MOR_WITHDRAW_LOCK_MS);
      }
    }
    return null;
  };
  const nextWithdrawDate = resolveNextWithdrawDate();

  const wrapperShadow = "0px 0px 1px 0px rgba(24,24,27,0.2), 0px 1px 2px 0px rgba(24,24,27,0.1)";

  if (!showFullWindow) {
    return (
      <Flex
        direction={{ base: "column", md: "row" }}
        width="full"
        bg="white"
        borderRadius="16px"
        boxShadow={wrapperShadow}
        p={{ base: "16px", md: "24px" }}
        gap={{ base: "16px", md: "0" }}
        alignItems={{ base: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Flex direction="column" gap="4px">
          <Text fontSize="20px" fontWeight={700} color="black">MOR Staking</Text>
          <Text fontSize="sm" color="#71717a">
            Stake MOR tokens to earn 15% APR rewards, CoinCap Points, and upgrade your API plan.
          </Text>
        </Flex>
        <Flex gap="12px">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setWhatIsModalVisible(true)}
          >
            Learn More
          </Button>
          <Button
            size="sm"
            colorPalette="purple"
            variant="solid"
            onClick={() => stakeMorAndConnectWallet(1, false)}
          >
            Stake MOR
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      width="full"
      bg="white"
      borderRadius="16px"
      boxShadow={wrapperShadow}
      p={{ base: "16px", md: "24px" }}
    >
      {/* Header Row: Title + actions (More Details + permanent Stake MOR CTA).
          Stacks on mobile so the title and both buttons don't overflow. */}
      <Flex
        width="full"
        gap={{ base: "12px", md: "6px" }}
        flexDir={{ base: "column", md: "row" }}
        alignItems={{ base: "flex-start", md: "center" }}
        justifyContent="space-between"
        px="8px"
        mb={4}
      >
        <Text fontSize="30px" fontWeight={700} lineHeight="32px" letterSpacing="-0.4px" color="black">
          MOR Staking
        </Text>
        <Flex gap="8px" alignItems="center" flexShrink={0}>
        <Flex
          as="button"
          bg="#f3e8ff"
          borderWidth="1px"
          borderColor="#e9d5ff"
          borderRadius="6px"
          height="40px"
          minWidth="40px"
          px="16px"
          py="2px"
          alignItems="center"
          justifyContent="center"
          gap="8px"
          cursor="pointer"
          flexShrink={0}
          onClick={() => setIsExpanded(!isExpanded)}
          _hover={{ bg: "#e9d5ff" }}
          transition="background 150ms"
        >
          <Text fontSize="14px" fontWeight={500} lineHeight="20px" color="#641ba3" textAlign="center">
            More Details
          </Text>
          <Box
            transition="transform 300ms ease-in-out"
            transform={isExpanded ? "rotate(180deg)" : "rotate(0deg)"}
            color="#641ba3"
          >
            <LuChevronDown size={20} />
          </Box>
        </Flex>
        {/* Permanent stake CTA — always visible regardless of the accordion
            state, so staking additional MOR is one click away. Routes to Base
            (Arbitrum staking is discontinued). */}
        <Button
          size="sm"
          colorPalette="purple"
          variant="solid"
          height="40px"
          px="16px"
          flexShrink={0}
          onClick={() => stakeMorAndConnectWallet(1, false)}
        >
          Stake MOR
        </Button>
        </Flex>
      </Flex>

      {/* Stat Cards Row */}
      <Flex
        width="full"
        gap={4}
        alignItems="center"
        flexDir={{ base: "column", md: "row" }}
        justifyContent="center"
      >
        {/* Staked MOR stat card */}
        <Flex
          flex={1}
          bg="white"
          borderRadius="16px"
          borderWidth="1px"
          borderColor="#e4e4e7"
          px="24px"
          py="12px"
          flexDir="column"
          alignItems="center"
          gap="2px"
          whiteSpace="nowrap"
        >
          <Text fontSize="36px" fontWeight={300} lineHeight="44px" letterSpacing="-0.4px" color="black">
            {totalStakedDisplay.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
          </Text>
          <Text fontSize="14px" fontWeight={500} lineHeight="20px" color="#a1a1aa" letterSpacing="0">
            Staked MOR
          </Text>
          {/* Per-chain split — only shown when a legacy Arbitrum position
              exists, so Base-only stakers keep the clean single number. */}
          {hasArbitrumStake && (
            <Flex direction="column" alignItems="center" gap="2px" mt="6px">
              <Flex alignItems="center" gap="6px">
                <img src="/external/base_icon.png" alt="Base" style={{ height: "12px" }} />
                <Text fontSize="12px" color="#52525b">
                  {baseStaked.toLocaleString(undefined, { maximumFractionDigits: 2 })} MOR
                </Text>
              </Flex>
              <Flex alignItems="center" gap="6px">
                <img src="/external/arbitrum_icon.png" alt="Arbitrum" style={{ height: "12px" }} />
                <Text fontSize="12px" color="#52525b">
                  {stakedArbitrumBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} MOR
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>

        {/* MOR Rewards stat card */}
        <Flex
          flex={1}
          bg="white"
          borderRadius="16px"
          borderWidth="1px"
          borderColor="#e4e4e7"
          px="24px"
          py="12px"
          flexDir="column"
          alignItems="center"
          gap="2px"
          whiteSpace="nowrap"
        >
          <Text fontSize="36px" fontWeight={300} lineHeight="44px" letterSpacing="-0.4px" color="black">
            {formatDecimals(displayedMorRewards, 4)}
          </Text>
          <Text fontSize="14px" fontWeight={500} lineHeight="20px" color="#a1a1aa" letterSpacing="0">
            MOR Rewards (15% APR)
          </Text>
        </Flex>

        {/* CoinCap Points stat card */}
        <Flex
          flex={1}
          bg="white"
          borderRadius="16px"
          borderWidth="1px"
          borderColor="#e4e4e7"
          px="24px"
          py="12px"
          flexDir="column"
          alignItems="center"
          gap="2px"
          whiteSpace="nowrap"
        >
          <Text fontSize="36px" fontWeight={300} lineHeight="44px" letterSpacing="-0.4px" color="black">
            {formatDecimals(displayedPoints, 2)}
          </Text>
          <Text fontSize="14px" fontWeight={500} lineHeight="20px" color="#a1a1aa" letterSpacing="0">
            CoinCap Points{info?.pointsPerDay ? ` (${formatDecimals(info.pointsPerDay, 2)}/Day)` : ""}
          </Text>
        </Flex>
      </Flex>

      {/* Inline accordion panel */}
      <Box
        overflow="hidden"
        transition="max-height 300ms ease-in-out, opacity 300ms ease-in-out"
        // The panel's three zones sit side-by-side on md+ (fits ~350px) but
        // STACK vertically on mobile (~600px). A single 400px ceiling clipped
        // the last zone — the Claim/Stake actions — on mobile, leaving users
        // unable to claim. Give the stacked mobile layout enough headroom.
        maxHeight={isExpanded ? { base: "800px", md: "400px" } : "0px"}
        opacity={isExpanded ? 1 : 0}
      >
        <Flex
          mt={3}
          bg="#fafafa"
          borderRadius="16px"
          borderWidth="1px"
          borderColor="#e4e4e7"
          flexDir={{ base: "column", md: "row" }}
          overflow="hidden"
        >
          {/* Zone A: Rewards Breakdown */}
          <Box
            flex={2}
            p={6}
            borderRightWidth={{ base: 0, md: "1px" }}
            borderBottomWidth={{ base: "1px", md: 0 }}
            borderColor="#e4e4e7"
          >
            <Text fontSize="xs" fontWeight={600} color="#a1a1aa" letterSpacing="0.05em" mb={2}>
              PENDING MOR REWARDS
            </Text>
            <Text fontSize="24px" fontWeight={700} lineHeight="32px" color="black">
              {formatDecimals(totalClaimableRewards, 4)}{" "}
              <Text as="span" fontSize="md" fontWeight={400} color="#52525b">
                Available
              </Text>
            </Text>
            <Text fontSize="sm" color="#a1a1aa" mb={4}>
              {formatDecimals((totalPendingRewards ?? 0) + availablePendingArbRewards, 4)} Total
            </Text>

            {/* Chain breakdown */}
            <Flex alignItems="center" gap={2} mb={4}>
              <Box width="8px" height="8px" borderRadius="full" bg="#0052FF" flexShrink={0} />
              <img src="/external/base_icon.png" alt="Base" style={{ height: "14px" }} />
              <Text fontSize="sm" color="#52525b">Base</Text>
              <Text fontSize="sm" fontWeight={600} color="black">
                {formatDecimals(availablePendingBaseRewards ?? 0, 4)} MOR
              </Text>
            </Flex>
            {availablePendingArbRewards > 0 && (
              <Flex alignItems="center" gap={2} mb={4}>
                <Box width="8px" height="8px" borderRadius="full" bg="#28A0F0" flexShrink={0} />
                <img src="/external/arbitrum_icon.png" alt="Arbitrum" style={{ height: "14px" }} />
                <Text fontSize="sm" color="#52525b">Arbitrum</Text>
                <Text fontSize="sm" fontWeight={600} color="black">
                  {formatDecimals(availablePendingArbRewards, 4)} MOR
                </Text>
              </Flex>
            )}

            {/* Next withdraw date */}
            {nextWithdrawDate && (
              <>
                <Text fontSize="xs" fontWeight={600} color="#a1a1aa" letterSpacing="0.05em" mb={1}>
                  NEXT WITHDRAW DATE
                </Text>
                <Text fontSize="sm" fontWeight={600} color="black">
                  {formatWithdrawDate(nextWithdrawDate)}
                </Text>
              </>
            )}
          </Box>

          {/* Zone B: Yield & Earnings */}
          <Box
            flex={1.5}
            p={6}
            borderRightWidth={{ base: 0, md: "1px" }}
            borderBottomWidth={{ base: "1px", md: 0 }}
            borderColor="#e4e4e7"
          >
            <Text fontSize="xs" fontWeight={600} color="#a1a1aa" letterSpacing="0.05em" mb={2}>
              YIELD
            </Text>
            <Flex alignItems="center" gap={2} mb={4}>
              <Text fontSize="24px" fontWeight={700} lineHeight="32px" color="black">
                15%
              </Text>
              <Badge colorPalette="green" variant="subtle" fontSize="xs">
                APR
              </Badge>
            </Flex>

            <Text fontSize="xs" fontWeight={600} color="#a1a1aa" letterSpacing="0.05em" mb={2}>
              ESTIMATED EARNINGS
            </Text>
            <Stack gap={1}>
              <Flex justifyContent="space-between">
                <Text fontSize="sm" color="#52525b">Daily</Text>
                <Text fontSize="sm" fontWeight={600} color="black">
                  ~{formatDecimals(morRewardsPerDay, 4)} MOR
                </Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text fontSize="sm" color="#52525b">Monthly</Text>
                <Text fontSize="sm" fontWeight={600} color="black">
                  ~{formatDecimals(morRewardsPerMonth, 2)} MOR
                </Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text fontSize="sm" color="#52525b">Yearly</Text>
                <Text fontSize="sm" fontWeight={600} color="black">
                  ~{formatDecimals(morRewardsPerYear, 2)} MOR
                </Text>
              </Flex>
            </Stack>
          </Box>

          {/* Zone C: Actions */}
          <Stack flex={1.5} p={6} gap={3} justifyContent="center">
            <Button
              variant="solid"
              colorPalette="purple"
              width="full"
              disabled={!canWithdrawAnyRewards}
              onClick={handleClaimRewards}
            >
              <LuUpload />
              {canWithdrawAnyRewards
                ? `Claim ${formatDecimals(totalClaimableRewards, 2)} MOR`
                : "No rewards to claim"}
            </Button>
            {/* Unstake — routes to the chain the user is staked on (Arbitrum is
                auto-detected via stakedArbitrumBalance and switched to 42161).
                Withdraws principal only; no rewards involved. Prefill the full
                staked amount only when the chain is unambiguous; if staked on
                BOTH chains, proceedToStake opens the SelectNetworkDialog, so a
                prefilled amount would be wrong for whichever chain the user
                picks — pass 0 and let them enter it against that chain. */}
            <Button
              variant="outline"
              width="full"
              disabled={!hasAnyStake}
              onClick={() => stakeMorAndConnectWallet(bothChainsStaked ? 0 : (hasArbitrumStake ? stakedArbitrumBalance : baseStaked), true)}
            >
              <LuUpload />
              Unstake MOR
            </Button>
            <Link
              fontSize="sm"
              color="#9333ea"
              fontWeight={500}
              display="flex"
              alignItems="center"
              gap="4px"
              alignSelf="center"
              cursor="pointer"
              onClick={() => setWhatIsModalVisible(true)}
            >
              <LuInfo size={14} />
              What is MOR Staking?
            </Link>
          </Stack>
        </Flex>
      </Box>
    </Flex>
  );
};

export default StakingStatsContainer;
